<script setup lang="ts">
  import GIDataFileUploadDialog from '@/components/projectDetail/giDataFileUpload/GIDataFileUploadDialog.vue'
  import DeleteDialog from '@/components/dialog/DeleteDialog.vue'
  import GIDataCollectionIndex from '@/components/projectDetail/GIDrawer/GIDataCollectionIndex.vue'
  import type { GIPointCollection } from '@/stores/type/giPoint.type'
  import type { SectionLine } from '@/stores/type/sectionLine.type'
  import HeaderWithActions from '@/components/projectDetail/components/HeaderWithActions.vue'
  import PrimaryButton from '@/components/shared/PrimaryButton.vue'
  import GIDataImportDialog from '@/components/projectDetail/giDataImport/GIDataImportDialog.vue'
  import ImportWarningsDialog from '@/components/dialog/ImportWarningsDialog.vue'
  import { useOpengroundUIStore } from '@/stores/opengroundUI'
  import ImportOpengroundDialog from './ImportOpengroundDialog.vue'

  const store = useProjectStore()
  const giDataUploadCollectionsStore = useGIDataUploadCollectionStore()
  const giPointsStore = useGIPointsStore()
  const sectionLinesStore = useSectionLinesStore()
  const sectionLinesGIPointsStore = useSectionLinesGIPointsStore()
  const giDrawer = ref<HTMLElement | null>(null)
  const { selectedItems: selectedGIUploadCollections } = storeToRefs(giDataUploadCollectionsStore)
  const { showSelectMap: showSelectSectionLineMap, selectedItems: selectedSectionLines } =
    storeToRefs(sectionLinesStore)
  const { selectedItems: selectedGIPoints } = storeToRefs(giPointsStore)
  const opengroundUIStore = useOpengroundUIStore()

  const isSelecting = computed(() => {
    return (
      selectedSectionLines.value[0] &&
      showSelectSectionLineMap.value[selectedSectionLines.value[0].id] === 'create'
    )
  })

  useResizeableElement({
    ref: giDrawer,
    widthProperty: 'groundInvestigationDrawer',
    elementGetter: vDrawerElementGetter
  })

  const onGIPointCollectionDelete = () => {
    selectedGIUploadCollections.value.forEach((giPointCollection: GIPointCollection) => {
      giPointCollection.gipoint_set.features.forEach((giPoint) => {
        giPointsStore.removeItem(giPoint)
      })
      sectionLinesStore.items.forEach((sectionLine: SectionLine) => {
        sectionLinesGIPointsStore.removeGIPointsFromSectionLine(
          giPointCollection.gipoint_set.features.map((giPoint) => giPoint.id),
          sectionLine.id
        )
      })
    })
    giDataUploadCollectionsStore.clearSelectedGIPointCollections()
  }

  const addGIPointSelections = async () => {
    if (selectedSectionLines.value[0]) {
      await sectionLinesGIPointsStore.addGIPointsToSectionLine(
        selectedGIPoints.value,
        selectedSectionLines.value[0].id
      )
      sectionLinesStore.toggleSelectionOption(selectedSectionLines.value[0].id, null)
    }
    store.toggleDrawerType('profileDrawer')
  }
</script>

<template>
  <v-navigation-drawer
    :temporary="true"
    :scrim="false"
    ref="giDrawer"
    v-if="store.groundInvestigationDrawer.shown"
    :model-value="store.groundInvestigationDrawer.shown"
    :width="store.groundInvestigationDrawer.width"
    :class="['gi-drawer', 'resize-handler-gi']"
  >
    <HeaderWithActions>
      <template #default> Ground Investigation Data Management </template>
      <template #actions>
        <PrimaryButton prepend-icon="mdi-plus">
          Add
          <v-menu activator="parent">
            <v-list>
              <v-list-item
                title="Receive from Speckle"
                prepend-icon="mdi-import"
                @click="giDataUploadCollectionsStore.toggleImportFromSpeckleDialog()"
              ></v-list-item>
              <v-list-item
                title="Upload files"
                prepend-icon="mdi-plus"
                @click="giDataUploadCollectionsStore.toggleUploadDialog()"
              ></v-list-item>
            </v-list>
          </v-menu>
        </PrimaryButton>
        <PrimaryButton @click="opengroundUIStore.showDialog = true" prepend-icon="mdi-import">
          OG2Speckle
        </PrimaryButton>
      </template>
      <template #subheader> Upload and manage ground investigation data </template>
    </HeaderWithActions>
    <v-divider></v-divider>
    <div v-if="isSelecting" class="d-flex flex-column ml-4 my-2">
      <div>
        You are selecting GI Points for section line
        <strong>{{
          selectedSectionLines[0] && selectedSectionLines[0].properties.section_title
        }}</strong>
      </div>
      <div class="d-flex justify-start align-center g-2 mt-2">
        <v-btn color="primary" size="x-small" variant="flat" @click.stop="addGIPointSelections">
          Create Selection
        </v-btn>
        <v-btn
          color="error"
          size="x-small"
          variant="outlined"
          @click.stop="() => {
            sectionLinesStore.toggleSelectionOption(selectedSectionLines[0]!.id, null)
            giPointsStore.setSelectedItems([])
            store.toggleDrawerType('profileDrawer')
          }"
        >
          Cancel
        </v-btn>
      </div>
    </div>

    <GIDataCollectionIndex :is-selecting="isSelecting" />
    <GIDataFileUploadDialog :store="giDataUploadCollectionsStore" />
    <GIDataImportDialog />
    <ImportWarningsDialog />
    <GotoJupyterHubDialog />
    <ImportOpengroundDialog />
    <DeleteDialog
      :store="giDataUploadCollectionsStore"
      @deleted="onGIPointCollectionDelete"
      :bulk-delete="true"
      :items-to-delete="selectedGIUploadCollections"
    />
  </v-navigation-drawer>
</template>

<style scoped>
  .resize-handler-gi {
    border-right-width: 10px;
    z-index: 1;
    box-shadow: none;
  }

  .hidden {
    display: none;
    opacity: 0;
    transition: opacity 0.5s ease;
  }

  :deep(.v-navigation-drawer__content) {
    overflow-y: hidden;
  }

  .gi-drawer {
    transition: border-bottom-color 0.3s;
    border-right: 10px solid rgba(0, 0, 0, 0.1);
  }
</style>
